// Firestore collection sync + onboarding-state + local-cache helpers.
import { getDocs } from "firebase/firestore";
import { userCol, LS } from "./firebase";

// Durable record of which uids have finished onboarding. Kept separate from the
// per-user profile cache so a missing/evicted `ufcm_profile_<uid>` can never make
// a returning user land back on the "Quick Setup" screen.
export const ONBOARDED_KEY = "ufcm_onboarded_uids";
export const isOnboarded = (uid) => { const a = LS.get(ONBOARDED_KEY); return Array.isArray(a) && a.includes(uid); };
export const markOnboarded = (uid) => {
  if (!uid) return;
  const a = LS.get(ONBOARDED_KEY);
  const arr = Array.isArray(a) ? a : [];
  if (!arr.includes(uid)) { arr.push(uid); LS.set(ONBOARDED_KEY, arr); }
};

// Whether the "Quick Setup" screen should show. Named + pure so the
// "never flash the signup form at a returning user" rule can be tested directly.
export const needsQuickSetup = ({ profileChecked, dataLoading, profile, uid }) =>
  !!profileChecked && !dataLoading && !profile?.profileComplete && !isOnboarded(uid);

// A cold Firestore offline cache resolves as an EMPTY snapshot (fromCache:true)
// instead of rejecting. When that happens, keep the rows we already cached — do
// not overwrite good data (e.g. the Dashboard ledger) with zeros.
export const shouldKeepCache = (prev, rowCount, fromCache) =>
  !!fromCache && rowCount === 0 && Array.isArray(prev) && prev.length > 0;

// The one cached-collection sync. Optionally seed state from the LS cache first
// (so a screen is never blank offline), then refresh from Firestore — but an
// empty offline snapshot never clobbers a good cache. Failures leave the seed.
export const syncCol = async (uid, col, setter, seedFirst = true) => {
  const lsKey = `ufcm_${col}_${uid}`;
  const cached = LS.get(lsKey);
  if (seedFirst && Array.isArray(cached)) setter(cached);
  try {
    const snap = await getDocs(userCol(uid, col));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (shouldKeepCache(cached, rows.length, snap.metadata?.fromCache)) return;
    // Unchanged since last sync → skip the setState (avoids a re-render of every
    // consumer on the interval) and the LS write.
    if (JSON.stringify(rows) === JSON.stringify(cached)) return;
    setter(rows);
    LS.set(lsKey, rows);
  } catch (e) {
    // "unavailable" = offline, expected. Anything else (permission-denied,
    // unauthenticated) is a real problem hiding behind stale data — log it.
    if (e?.code && e.code !== "unavailable") console.error(`sync ${col} failed:`, e.code);
  }
};

// Per-user localStorage caches (keyed `ufcm_<col>_<uid>`).
export const CACHE_COLS = ["profile", "clients", "appointments", "payments", "attendance"];
export const clearLocalCache = (uid) => CACHE_COLS.forEach((c) => LS.set(`ufcm_${c}_${uid}`, null));

// True in local dev, Playwright, and the Capacitor WebView (served from
// https://localhost) — but NOT on the deployed web PWA. Gates the test seams.
export const IS_LOCAL = typeof window !== "undefined" &&
  /^(localhost|127\.|\[::1\])/.test(window.location.hostname || "");
if (IS_LOCAL) {
  window.__prabzo = { isOnboarded, markOnboarded, needsQuickSetup, shouldKeepCache };
}
